import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Alert,
  Card,
  CardActions,
  CardContent,
  Fade,
  Zoom,
  Slide,
  LinearProgress,
  Chip,
  Avatar,
  Stack,
  Divider,
  useTheme,
  alpha,
  Collapse
} from '@mui/material';
import {
  CloudUpload,
  TouchApp,
  CheckCircle,
  FileUpload,
  Speed,
  Bookmark,
  TrendingUp,
  AutoStories,
  Language,
  Timeline,
  PlayArrow,
  CloudDone,
  School,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  GTranslate,
  MenuBook,
  Search,
  Download
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Link as RouterLink } from 'react-router-dom';
import TextReader from './TextReader';
import RecentDocuments from './library/RecentDocuments';
import HeroMarkAurora from './HeroMarkAurora';
import HeroMarkLiquidGlass from './HeroMarkLiquidGlass';
import { keyframes } from '@mui/system';
import { uploadDocument } from '../services/documentStorageService';
import { trackEvent } from '../services/analyticsService';

const float = keyframes`
  0%, 100% { transform: translateY(0) }
  50% { transform: translateY(-6px) }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(102,126,234,0.25) }
  50% { box-shadow: 0 0 35px rgba(118,75,162,0.45) }
`;

const HomeDashboard = () => {
  const { user, isPremium } = useAuth();
  const theme = useTheme();
  const HERO_MARK_VARIANT = 'liquidGlass';
  const HeroMark = HERO_MARK_VARIANT === 'aurora' ? HeroMarkAurora : HeroMarkLiquidGlass;
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showReader, setShowReader] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showFeatureDetails, setShowFeatureDetails] = useState({});
  const [animationLoaded, setAnimationLoaded] = useState(false);
  const [upgradePrompt, setUpgradePrompt] = useState(null);

  // Animation trigger
  useEffect(() => {
    const timer = setTimeout(() => setAnimationLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const shouldScroll = sessionStorage.getItem('scrollToPricing');
    if (shouldScroll) {
      sessionStorage.removeItem('scrollToPricing');
      setTimeout(() => {
        const el = document.getElementById('pricing-section');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    }
  }, []);

  // Check for document to open from library via sessionStorage
  useEffect(() => {
    const openDocumentData = sessionStorage.getItem('openDocument');
    if (openDocumentData) {
      try {
        const { file, document, fileUrl } = JSON.parse(openDocumentData);
        
        // Create a File object from the stored URL
        fetch(fileUrl)
          .then(response => response.blob())
          .then(blob => {
            const fileObj = new File([blob], file.name, { type: file.type });
            setSelectedFile(fileObj);
            setSelectedDocument(document);
            setShowReader(true);
            
            // Clear the sessionStorage after using it
            sessionStorage.removeItem('openDocument');
          })
          .catch(error => {
            console.error('Error loading document from library:', error);
            sessionStorage.removeItem('openDocument');
          });
      } catch (error) {
        console.error('Error parsing document data:', error);
        sessionStorage.removeItem('openDocument');
      }
    }
  }, []);

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      handleMultipleFiles(files);
    }
  };

  const validateFile = (file) => {
    const allowedTypes = ['.pdf', '.txt', '.md'];
    const maxSize = 50 * 1024 * 1024; // 50MB
    
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      return { valid: false, error: `File type ${fileExtension} not supported. Please use PDF, TXT, or MD files.` };
    }
    
    if (file.size > maxSize) {
      return { valid: false, error: `File size too large. Maximum size is 50MB.` };
    }
    
    return { valid: true };
  };

  const handleMultipleFiles = (files) => {
    const validFiles = [];
    const errors = [];
    
    files.forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${validation.error}`);
      }
    });
    
    if (errors.length > 0) {
      // Handle errors - could show in snackbar or alert
      console.error('File validation errors:', errors);
    }
    
    if (validFiles.length > 0) {
      // For now, just handle the first valid file
      handleSingleFile(validFiles[0]);
    }
  };

  const handleSingleFile = (file) => {
    if (user) {
      handleLibraryUploadAndOpen(file);
    } else {
      setSelectedFile(file);
      setSelectedDocument(null);
      setShowReader(true);

      trackEvent('reader_opened', {
        source: 'home_local_file',
        fileType: file?.type || 'unknown',
        stored: false,
      });
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleSingleFile(file);
    }
  };

  const handleLibraryUploadAndOpen = async (file) => {
    try {
      setUploading(true);
      setUploadProgress(10);
      const result = await uploadDocument(file, user);
      setUploadProgress(90);
      console.log('File uploaded to library successfully');
      
      // Now open the reader with the uploaded document
      setSelectedFile(file);
      setSelectedDocument(result.document);
      setUploadProgress(100);
      setShowReader(true);

      trackEvent('reader_opened', {
        source: 'home_upload',
        fileType: file?.type || 'unknown',
        stored: true,
      });
    } catch (error) {
      console.error('Failed to upload to library:', error);
      if (error?.code === 'LIBRARY_DOC_CAP_REACHED') {
        setUpgradePrompt({
          title: 'Library limit reached',
          message: error.message || 'Free plan allows up to 3 saved documents. Upgrade to save more.',
        });
      } else if (error?.code === 'LIBRARY_SIZE_CAP_REACHED') {
        setUpgradePrompt({
          title: 'Storage full',
          message: error.message || 'Free plan storage is full. Upgrade to save larger libraries.',
        });
      }

      // Even if upload fails, still allow reading the file locally
      setSelectedFile(file);
      setSelectedDocument(null);
      setShowReader(true);

      trackEvent('reader_opened', {
        source: 'home_upload',
        fileType: file?.type || 'unknown',
        stored: false,
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSeePlans = () => {
    window.location.assign('/usage?plan=yearly');
  };

  const handleOpenDocument = (file, document) => {
    setSelectedFile(file);
    setSelectedDocument(document);
    setShowReader(true);

    trackEvent('reader_opened', {
      source: 'home_recent',
      fileType: file?.type || document?.file_type || 'unknown',
      stored: !!document,
    });
  };

  const handleBackToHome = () => {
    setShowReader(false);
    setSelectedFile(null);
    setSelectedDocument(null);
  };

  const handleTrySampleDocument = () => {
    const sampleText = `Lexory sample text\n\nRead naturally. Click any word you don’t understand.\n\nLa lectura en otro idioma se vuelve más fácil cuando solo traduces lo que necesitas.\n\nEssayez de cliquer sur un mot, puis continuez à lire.\n\nBuona lettura!`;

    const sampleFile = new File([sampleText], 'lexory-sample.txt', { type: 'text/plain' });

    setSelectedFile(sampleFile);
    setSelectedDocument(null);
    setShowReader(true);

    trackEvent('reader_opened', {
      source: 'sample',
      fileType: sampleFile?.type || 'text/plain',
      stored: false,
    });
  };

  if (showReader) {
    return (
      <Container maxWidth={false} sx={{ maxWidth: '1760px' }}>
        <Box sx={{ mb: 2 }}>
          <Button onClick={handleBackToHome} variant="outlined">
            ← Back to Home
          </Button>
        </Box>
        {upgradePrompt && (
          <Alert
            severity="info"
            sx={{ mb: 2, borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleSeePlans} sx={{ fontWeight: 700 }}>
                See premium plans
              </Button>
            }
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {upgradePrompt.title}
            </Typography>
            <Typography variant="body2">
              {upgradePrompt.message}
            </Typography>
          </Alert>
        )}
        <TextReader 
          initialFile={selectedFile} 
          initialDocument={selectedDocument}
        />
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.1)',
            zIndex: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-20%',
            width: '40%',
            height: '200%',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '50%',
            zIndex: 1,
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in={animationLoaded} timeout={1000}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <HeroMark />

              {/* Language flag ribbon */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: { xs: '6px', sm: '10px', md: '14px' },
                  mb: 2.5,
                  flexWrap: 'wrap',
                }}
              >
                {[
                  { code: 'us', title: 'English' },
                  { code: 'es', title: 'Spanish' },
                  { code: 'br', title: 'Portuguese' },
                  { code: 'fr', title: 'French' },
                  { code: 'de', title: 'German' },
                  { code: 'it', title: 'Italian' },
                  { code: 'jp', title: 'Japanese' },
                  { code: 'cn', title: 'Chinese' },
                  { code: 'kr', title: 'Korean' },
                  { code: 'sa', title: 'Arabic' },
                  { code: 'ru', title: 'Russian' },
                  { code: 'in', title: 'Hindi' },
                ].map((f, i) => (
                  <Box
                    key={f.code}
                    component="img"
                    src={`https://flagcdn.com/w40/${f.code}.png`}
                    srcSet={`https://flagcdn.com/w80/${f.code}.png 2x`}
                    alt={f.title}
                    title={f.title}
                    sx={{
                      width: { xs: 26, sm: 30, md: 34 },
                      height: { xs: 19, sm: 22, md: 25 },
                      borderRadius: '3px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                      opacity: animationLoaded ? 0.92 : 0,
                      transform: animationLoaded ? 'translateY(0)' : 'translateY(8px)',
                      transition: `opacity 0.5s ease ${0.15 + i * 0.06}s, transform 0.5s ease ${0.15 + i * 0.06}s`,
                      '&:hover': {
                        opacity: 1,
                        transform: 'translateY(-2px) scale(1.12)',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
                      },
                      transitionProperty: 'opacity, transform, box-shadow',
                    }}
                  />
                ))}
              </Box>

              <Typography
                variant="overline"
                sx={{
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  fontWeight: 500,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  opacity: 0.9,
                  mb: 2,
                  display: 'block',
                }}
              >
                Master languages naturally—one click at a time
              </Typography>

              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                Read Anything. Understand Everything.
              </Typography>
              
              <Typography
                variant="h5"
                sx={{
                  opacity: 0.95,
                  maxWidth: 800,
                  mx: 'auto',
                  fontWeight: 300,
                  lineHeight: 1.6,
                  mb: 4,
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                }}
              >
                Click any word or highlight a phrase to translate instantly. Saved words become smart flashcards with audio — so you actually remember what you learn.
              </Typography>

              {/* Quick Stats */}
              <Grid container spacing={4} sx={{ justifyContent: 'center', mb: 6 }}>
                <Grid item xs={4} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        background: 'linear-gradient(135deg, #ffffff 0%, #d4b8ff 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      69
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      languages supported
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
                      <Speed sx={{ fontSize: { xs: 48, md: 64 } }} />
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      instant translations
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
                      <Bookmark sx={{ fontSize: { xs: 48, md: 64 } }} />
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      auto-saved vocabulary
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrow />}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    animation: `${pulseGlow} 3s ease-in-out infinite`,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.3)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                      animation: 'none',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onClick={() => document.getElementById('upload-section').scrollIntoView({ behavior: 'smooth' })}
                >
                  Start Reading for Free
                </Button>
                <Button
                  variant="text"
                  size="large"
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    px: 3,
                    py: 2,
                    fontSize: '1rem',
                    fontWeight: 500,
                    textDecoration: 'underline',
                    textUnderlineOffset: '4px',
                    '&:hover': {
                      color: 'white',
                      bgcolor: 'transparent',
                      textDecoration: 'underline',
                    },
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => {
                    const el = document.getElementById('pricing-section');
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  See plans
                </Button>
              </Stack>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: -8, position: 'relative', zIndex: 10, pb: 8 }}>
        {/* How it works */}
        <Fade in={animationLoaded} timeout={1100}>
          <Card
            elevation={0}
            sx={{
              mb: 6,
              borderRadius: 4,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 20px 60px rgba(0,0,0,0.4)'
                : '0 20px 60px rgba(0,0,0,0.10)',
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'visible',
            }}
          >
            <CardContent sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
              <Typography
                variant="h3"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  display: 'inline-block',
                  lineHeight: 1.15,
                  pb: 0.5,
                  mb: 1.5,
                  overflow: 'visible',
                }}
              >
                How it works
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 720, mx: 'auto', lineHeight: 1.6 }}
              >
                Read real content in another language without stopping your flow.
              </Typography>

              <Grid container spacing={3} sx={{ mt: 1, justifyContent: 'center' }}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%', borderRadius: 3, boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.06)', bgcolor: 'background.default', position: 'relative', overflow: 'visible' }}>
                    <Chip label="1" size="small" sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontWeight: 800, bgcolor: 'primary.main', color: 'white', width: 28, height: 28 }} />
                    <CardContent sx={{ p: 4 }}>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main', mb: 2, mx: 'auto' }}>
                        <CloudUpload />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Upload your content
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        PDFs, documents, or text you actually want to read.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%', borderRadius: 3, boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.06)', bgcolor: 'background.default', position: 'relative', overflow: 'visible' }}>
                    <Chip label="2" size="small" sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontWeight: 800, bgcolor: 'secondary.main', color: 'white', width: 28, height: 28 }} />
                    <CardContent sx={{ p: 4 }}>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.12), color: 'secondary.main', mb: 2, mx: 'auto' }}>
                        <TouchApp />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Click to translate instantly
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        Click a word or highlight a phrase — no switching tabs, no copy-paste.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%', borderRadius: 3, boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.06)', bgcolor: 'background.default', position: 'relative', overflow: 'visible' }}>
                    <Chip label="3" size="small" sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontWeight: 800, bgcolor: 'success.main', color: 'white', width: 28, height: 28 }} />
                    <CardContent sx={{ p: 4 }}>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.12), color: 'success.main', mb: 2, mx: 'auto' }}>
                        <Bookmark />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Learn naturally
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        Every translated word is saved. Review with smart flashcards, hear pronunciation, and master vocabulary at your pace.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Fade>

        {/* Enhanced Drag & Drop Upload Section */}
        <Slide in={animationLoaded} direction="up" timeout={1200}>
          <Card
            id="upload-section"
            sx={{
              mb: 6,
              borderRadius: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              background: dragActive 
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`
                : 'background.paper',
              border: dragActive ? `2px dashed ${theme.palette.primary.main}` : '2px dashed transparent',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: dragActive ? 'scale(1.02)' : 'scale(1)',
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <CardContent sx={{ p: { xs: 4, md: 8 }, textAlign: 'center' }}>
              <Box sx={{ mb: 4 }}>
                <Avatar
                  sx={{
                    width: { xs: 80, md: 120 },
                    height: { xs: 80, md: 120 },
                    mx: 'auto',
                    mb: 3,
                    bgcolor: dragActive ? 'primary.main' : alpha(theme.palette.primary.main, 0.1),
                    transition: 'all 0.3s ease',
                    transform: dragActive ? 'scale(1.1) rotate(5deg)' : 'scale(1)',
                    animation: dragActive ? 'none' : `${float} 3s ease-in-out infinite`,
                  }}
                >
                  {dragActive ? (
                    <CloudDone sx={{ fontSize: { xs: 40, md: 60 }, color: 'white' }} />
                  ) : (
                    <CloudUpload sx={{ fontSize: { xs: 40, md: 60 }, color: 'primary.main' }} />
                  )}
                </Avatar>
              </Box>

              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  ...(dragActive
                    ? { color: 'primary.main' }
                    : {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        display: 'inline-block',
                      }),
                  mb: 2,
                }}
              >
                {dragActive ? 'Drop your files here!' : 'Drop in something to read'}
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 4, maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}
              >
                {dragActive 
                  ? 'Release to open your document in the Reader'
                  : 'Upload a PDF or text file. Click words as you go—learn without breaking your flow.'
                }
              </Typography>


              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  component="label"
                  size="large"
                  startIcon={<FileUpload />}
                  disabled={uploading}
                  sx={{
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(103, 126, 234, 0.3)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px rgba(103, 126, 234, 0.4)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  {uploading ? 'Uploading...' : 'Upload file'}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.txt,.md"
                    onChange={handleFileUpload}
                    multiple
                  />
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<PlayArrow />}
                  sx={{
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 3,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onClick={handleTrySampleDocument}
                >
                  Try a sample
                </Button>
              </Stack>

              {uploading && (
                <Box sx={{ mt: 3, maxWidth: 400, mx: 'auto' }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Uploading... {uploadProgress}%
                  </Typography>
                </Box>
              )}

              {/* User Status Hint */}
              <Box sx={{ mt: 3 }}>
                {user ? (
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.7,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.75,
                    }}
                  >
                    <Bookmark sx={{ fontSize: 18, opacity: 0.8 }} />
                    Your files are automatically saved to your library
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      opacity: 0.65,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.75,
                    }}
                  >
                    <TrendingUp sx={{ fontSize: 18, opacity: 0.8 }} />
                    Sign in to save files and track your reading progress
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Slide>

        {/* Recent Documents Section */}
        <Fade in={animationLoaded} timeout={1400}>
          <Box sx={{ mb: 6 }}>
            <RecentDocuments onOpenDocument={handleOpenDocument} />
          </Box>
        </Fade>

        {/* Enhanced Features Section */}
        <Fade in={animationLoaded} timeout={1600}>
          <Box id="demo-section" sx={{ mb: 6 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h3"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  display: 'inline-block',
                  lineHeight: 1.15,
                  pb: 0.5,
                  mb: 2,
                  overflow: 'visible',
                }}
              >
                Made for learning while you read
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}
              >
                Upload content you care about, translate in context, and build vocabulary without breaking your flow.
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {[
                {
                  icon: <Language />,
                  title: 'Click to translate instantly',
                  description: 'Click a word or highlight a phrase — get the translation immediately and keep reading.',
                  color: 'primary',
                  features: ['Word + phrase translation', '60+ languages', 'In-context popups', 'Save words automatically']
                },
                {
                  icon: <AutoStories />,
                  title: 'Upload your content',
                  description: 'Bring PDFs or text you actually want to read — then learn from real context.',
                  color: 'secondary',
                  features: ['PDF + text support', 'Bookmarks', 'Progress tracking', 'Comfortable reading']
                },
                {
                  icon: <Timeline />,
                  title: 'Learn naturally',
                  description: 'Every translated word is saved so you can review and export your vocabulary later.',
                  color: 'success',
                  features: ['Automatic word saving', 'Review later', 'Export your words', 'Keep your place']
                },
                {
                  icon: <School />,
                  title: 'Smart Flashcards',
                  description: 'Turn saved words into flashcards with spaced repetition. Hear pronunciation with text-to-speech and master vocabulary at your own pace.',
                  color: 'warning',
                  features: ['Spaced repetition algorithm', 'Text-to-speech audio', 'Import & export decks', 'Track your progress']
                }
              ].map((feature, index) => (
                <Grid item xs={12} md={3} key={index}>
                  <Zoom in={animationLoaded} timeout={800 + index * 200}>
                    <Card
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.08)',
                        bgcolor: 'background.default',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        border: '1px solid transparent',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: theme.palette.mode === 'dark' ? '0 20px 60px rgba(0,0,0,0.4)' : '0 20px 60px rgba(0,0,0,0.15)',
                          borderColor: alpha(theme.palette[feature.color].main, 0.4),
                        },
                      }}
                      onClick={() => setShowFeatureDetails(prev => ({
                        ...prev,
                        [index]: !prev[index]
                      }))}
                    >
                      <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        <Avatar
                          sx={{
                            width: 80,
                            height: 80,
                            mx: 'auto',
                            mb: 3,
                            bgcolor: `${feature.color}.main`,
                            boxShadow: `0 8px 32px ${alpha(theme.palette[feature.color].main, 0.3)}`,
                          }}
                        >
                          {React.cloneElement(feature.icon, { sx: { fontSize: 40, color: 'white' } })}
                        </Avatar>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                          {feature.title}
                        </Typography>

                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                          {feature.description}
                        </Typography>

                        <Collapse in={showFeatureDetails[index]}>
                          <Divider sx={{ mb: 2 }} />
                          <Stack spacing={1}>
                            {feature.features.map((item, idx) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CheckCircle sx={{ fontSize: 16, color: `${feature.color}.main`, mr: 1 }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {item}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Collapse>

                        <Button
                          size="small"
                          endIcon={showFeatureDetails[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          sx={{ mt: 2 }}
                        >
                          {showFeatureDetails[index] ? 'Show Less' : 'Learn More'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Fade>

        <Fade in={animationLoaded} timeout={1700}>
          <Box id="pricing-section" sx={{ mb: 6 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 20px 60px rgba(0,0,0,0.4)'
                  : '0 20px 60px rgba(0,0,0,0.10)',
                bgcolor: 'background.paper',
                border: `1px solid ${theme.palette.divider}`,
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Typography
                    variant="h3"
                    component="h2"
                    gutterBottom
                    sx={{
                      fontWeight: 800,
                      letterSpacing: '-0.02em',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      display: 'inline-block',
                      lineHeight: 1.15,
                      pb: 0.5,
                      mb: 1.5,
                      overflow: 'visible',
                    }}
                  >
                    Pricing
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 720, mx: 'auto', lineHeight: 1.6 }}>
                    {isPremium ? '💎 You\'re a Premium member. Thank you for your support!' : 'Don\'t let words you looked up yesterday disappear tomorrow.'}
                  </Typography>
                </Box>

                {/* Free Plan */}
                <Card sx={{ mb: 4, borderRadius: 3, boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.06)', bgcolor: 'background.default' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={2}>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>Free</Typography>
                          {!isPremium && <Chip label="Great for trying" size="small" variant="outlined" />}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          50 words/day • Up to 15 words per request
                        </Typography>
                      </Box>
                      {isPremium ? (
                        <Chip label="Previous plan" size="small" variant="outlined" />
                      ) : (
                        <Button
                          variant="outlined"
                          component={RouterLink}
                          to={user ? '/reader' : '/login?mode=signup'}
                          sx={{ fontWeight: 700, borderRadius: 2 }}
                        >
                          {user ? 'Current Plan' : 'Sign Up Free'}
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

                {/* Premium Plans */}
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
                  Engineered for mastery
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                  Everything you need to read, learn, and remember — in any language.
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    { icon: <GTranslate sx={{ fontSize: 22, color: 'primary.main' }} />, title: 'Unlimited translations', desc: 'Click any word or highlight phrases — no daily limits' },
                    { icon: <School sx={{ fontSize: 22, color: 'warning.main' }} />, title: 'Smart flashcards', desc: 'Spaced repetition + audio pronunciation built in' },
                    { icon: <MenuBook sx={{ fontSize: 22, color: 'success.main' }} />, title: 'Full document library', desc: 'Upload PDFs & texts — pick up right where you left off' },
                    { icon: <Timeline sx={{ fontSize: 22, color: 'info.main' }} />, title: 'Progress tracking', desc: 'See your vocabulary grow with stats & streaks' },
                    { icon: <Search sx={{ fontSize: 22, color: 'secondary.main' }} />, title: 'Search & favorites', desc: 'Find any saved word instantly, star what matters' },
                    { icon: <Download sx={{ fontSize: 22, color: 'error.main' }} />, title: 'Export everything', desc: 'Download your vocabulary, flashcards & progress anytime' },
                  ].map((f) => (
                    <Grid item xs={6} md={4} key={f.title}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5 }}>
                        <Box sx={{ mt: 0.25 }}>{f.icon}</Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{f.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{f.desc}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center', fontStyle: 'italic' }}>
                  Lock in this pricing before it changes.
                </Typography>

                <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
                  {/* Flex - Monthly */}
                  <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', borderRadius: 3, boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.06)', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ p: 3, flexGrow: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                          Flexible
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                          $7.99
                          <Typography component="span" variant="body2" color="text.secondary">
                            /mo
                          </Typography>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Billed monthly — $36 more per year than Max
                        </Typography>
                        <Stack spacing={0.75}>
                          {['Unlimited translations', 'Saved vocabulary', 'Smart flashcards', 'Document library'].map((item) => (
                            <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <CheckCircle sx={{ fontSize: 14, color: 'text.disabled' }} />
                              <Typography variant="body2" color="text.secondary">{item}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      </CardContent>
                      <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
                        {isPremium ? (
                          <Button fullWidth variant="contained" disabled sx={{ py: 1.2, fontWeight: 700, borderRadius: 2 }}>
                            ✔ Your Plan
                          </Button>
                        ) : (
                          <Button
                            fullWidth
                            variant="outlined"
                            component={RouterLink}
                            to={user ? '/usage?plan=monthly' : '/login'}
                            sx={{ py: 1.2, fontWeight: 700, borderRadius: 2 }}
                          >
                            Get Started
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>

                  {/* Plus - Quarterly */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.06)',
                        bgcolor: 'background.default',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                      }}
                    >
                      <Chip
                        label="Save 13%"
                        size="medium"
                        color="primary"
                        sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 700, fontSize: '0.85rem' }}
                      />
                      <CardContent sx={{ p: 3, flexGrow: 1 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                          Plus
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                          $6.99
                          <Typography component="span" variant="body2" color="text.secondary">
                            /mo
                          </Typography>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          $21 every 3 months • Save $3/mo
                        </Typography>
                        <Stack spacing={0.75}>
                          {['Everything in Flexible', 'Save $3/mo vs monthly', 'No long commitment'].map((item) => (
                            <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <CheckCircle sx={{ fontSize: 14, color: 'primary.main' }} />
                              <Typography variant="body2" color="text.secondary">{item}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      </CardContent>
                      <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
                        {isPremium ? (
                          <Button fullWidth variant="contained" disabled sx={{ py: 1.2, fontWeight: 700, borderRadius: 2 }}>
                            ✔ Your Plan
                          </Button>
                        ) : (
                          <Button
                            fullWidth
                            variant="outlined"
                            component={RouterLink}
                            to={user ? '/usage?plan=quarterly' : '/login'}
                            sx={{
                              py: 1.2,
                              fontWeight: 700,
                              borderRadius: 2,
                              boxShadow: '0 0 20px rgba(0, 188, 212, 0.4), 0 0 10px rgba(0, 188, 212, 0.25)',
                              '&:hover': {
                                boxShadow: '0 0 28px rgba(0, 188, 212, 0.55), 0 0 14px rgba(0, 188, 212, 0.35)',
                              },
                            }}
                          >
                            Get Plus
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>

                  {/* Max - Yearly */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 12px 48px rgba(118, 75, 162, 0.4)'
                          : '0 12px 48px rgba(118, 75, 162, 0.25)',
                        border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                        bgcolor: 'background.default',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        overflow: 'visible',
                      }}
                    >
                      <Chip
                        label="⭐ MOST POPULAR"
                        size="medium"
                        sx={{
                          position: 'absolute',
                          top: { xs: -14, md: -16 },
                          left: '50%',
                          transform: 'translateX(-50%)',
                          zIndex: 2,
                          fontWeight: 800,
                          fontSize: { xs: '0.75rem', md: '0.85rem' },
                          letterSpacing: '0.06em',
                          px: 2,
                          py: 0.5,
                          height: 'auto',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                          color: '#fff',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '20px',
                          boxShadow: '0 0 18px rgba(118, 75, 162, 0.5), 0 0 8px rgba(102, 126, 234, 0.3)',
                          animation: `${pulseGlow} 3s ease-in-out infinite`,
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                      <Chip
                        label="Save 41%"
                        size="small"
                        color="primary"
                        sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 700, fontSize: '0.8rem' }}
                      />
                      <CardContent sx={{ p: 3, flexGrow: 1 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                          Max — Unlock everything. No limits.
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                          $4.75
                          <Typography component="span" variant="body2" color="text.secondary">
                            /mo
                          </Typography>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Less than $1.10/week • 365 days of full access
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main', mt: 0.5, mb: 2 }}>
                          Save $39 compared to monthly
                        </Typography>
                        <Stack spacing={0.75}>
                          {['Everything in Plus', 'Lowest price guaranteed', 'Best long-term value'].map((item) => (
                            <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                              <Typography variant="body2" color="text.secondary">{item}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      </CardContent>
                      <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
                        {isPremium ? (
                          <Button fullWidth variant="contained" disabled sx={{ py: 1.2, fontWeight: 700, borderRadius: 2 }}>
                            ✔ Your Plan
                          </Button>
                        ) : (
                          <Button
                            fullWidth
                            variant="contained"
                            component={RouterLink}
                            to={user ? '/usage?plan=yearly' : '/login'}
                            sx={{
                              py: 1.2,
                              fontWeight: 700,
                              borderRadius: 2,
                              boxShadow: '0 10px 30px rgba(103, 126, 234, 0.35)',
                            }}
                          >
                            Unlock Everything
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                </Grid>

                {!user && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                    Join thousands of learners already growing with Lexory.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default HomeDashboard;
